import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ResistanceManagementData, CreateProductPayload, UpdateProductPayload, EditableProduct } from '../../models/admin-resistance.interface';
import { ResistanceComponentState } from '../../models';
import { API_ENDPOINTS, buildApiUrl } from '../../constants/apiendpoints.constant';

@Injectable({
  providedIn: 'root'
})
export class ResistanceManagementService {
  private cachedDataSubject = new BehaviorSubject<ResistanceManagementData | null>(null);
  public cachedData$ = this.cachedDataSubject.asObservable();
  private componentStateSubject = new BehaviorSubject<ResistanceComponentState | null>(null);
  public componentState$ = this.componentStateSubject.asObservable();

  constructor(private http: HttpClient) { }

  getResistanceProducts(forceRefresh: boolean = false): Observable<ResistanceManagementData> {
    const cachedData = this.cachedDataSubject.value;
    if (cachedData && !forceRefresh) {
      return of(cachedData);
    }
    const url = buildApiUrl(API_ENDPOINTS.RESISTANCE_MANAGEMENT.GET_RATING);
    return this.http.get<any>(url).pipe(
      map((response: any) => {
        const result = response.ResultBody || response;
        return {
          dropdownOptions: JSON.parse(result.dropdownOptions),
          tableData: JSON.parse(result.tableData)
        };
      }),
      tap((data: ResistanceManagementData) => {
        this.cachedDataSubject.next(data);
      })
    );
  }

  getCachedData(): ResistanceManagementData | null {
    return this.cachedDataSubject.value;
  }

  clearCache(): void {
    this.cachedDataSubject.next(null);
    this.componentStateSubject.next(null);
  }

  saveComponentState(state: ResistanceComponentState): void {
    this.componentStateSubject.next(state);
  }

  getComponentState(): ResistanceComponentState | null {
    return this.componentStateSubject.value;
  }

  clearComponentState(): void {
    this.componentStateSubject.next(null);
  }

  createProduct(payload: CreateProductPayload): Observable<any> {
    const url = buildApiUrl(API_ENDPOINTS.RESISTANCE_MANAGEMENT.SAVE_PRODUCT);
    return this.http.post<any>(url, payload);
  }

  updateProduct(payload: UpdateProductPayload): Observable<any> {
    const url = buildApiUrl(API_ENDPOINTS.RESISTANCE_MANAGEMENT.UPDATE_PRODUCT);
    return this.http.post<any>(url, payload);
  }

  deleteProduct(productId: number): Observable<any> {
    const url = buildApiUrl(API_ENDPOINTS.RESISTANCE_MANAGEMENT.DELETE_PRODUCT) + `?id=${productId}`;
    return this.http.post<any>(url, {});
  }

  // Helper method to convert EditableProduct to API payload
  private createPayloadFromProduct(product: EditableProduct, currentUser: string): CreateProductPayload {
    return {
      Crop_Name: product.Crop_Name, // Pass actual crop name
      Product_Name: product.Product_Name,
      Product_Type: product.Product_Type, // Pass actual product type
      Product_Number: Number(product.Product_Number) || 0,
      ESOA: Number(product.ESOA),
      PRE: Number(product.PRE),
      Rate_1: Number(product.Rate_1),
      Rate_2: Number(product.Rate_2),
      Full_Rate: Number(product.Full_Rate),
      Product_Image_Url: product.Product_Image_Url || '',
      Current_User: currentUser
    };
  }

  // Create payload for save operation
  createSavePayload(product: EditableProduct, currentUser: string): CreateProductPayload {
    return this.createPayloadFromProduct(product, currentUser);
  }

  // Create payload for update operation (includes ID)
  createUpdatePayload(product: EditableProduct, currentUser: string): UpdateProductPayload {
    return {
      ...this.createPayloadFromProduct(product, currentUser),
      Id: product.Id || 0
    };
  }
}

